import {
  ArrayField,
  BooleanField,
  BooleanInput,
  Button,
  Create,
  CreateButton,
  Datagrid,
  DatagridBody,
  DatagridRow,
  DatagridRowProps,
  DateField,
  DeleteWithConfirmButton,
  email,
  FieldProps,
  Form,
  FormDataConsumer,
  Link,
  ReferenceArrayInput,
  ReferenceField,
  required,
  SimpleForm,
  TextField,
  useGetIdentity,
  useLocaleState,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
  ReferenceInput,
  useChoicesContext,
  useGetOne,
  useListContext,
  useUnselectAll,
} from 'react-admin';
import { canAccess } from '@react-admin/ra-rbac';
import {
  Dispatch,
  SetStateAction, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FieldValues, useFormContext, useFormState,
} from 'react-hook-form';
import {
  Button as MuiButton,
  Checkbox,
  InputLabel, Tooltip,
  Typography,
  CardContent,
  Stack,
  Chip,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import _, { get } from 'lodash';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import CircularProgress from '@mui/material/CircularProgress';
import LoopIcon from '@mui/icons-material/Loop';
import {
  Asset, CertificationCategory, CertificationProperties, CustomerUserRelation, User,
} from '@x-guard/xgac-types/xgac';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { ReferenceOneInput } from '@react-admin/ra-relationships';
import moment from 'moment';
import { useGetList, useGetMany, usePermissions } from 'ra-core';
import { ListLive } from '@react-admin/ra-realtime';
import SearchFields from '../components/fields/Filters';
import { DocumentaryProofField, DocumentaryProofInputPreview } from '../components/DocumentaryProof';
import { LIST_DEBOUNCE, XGAC_MAIN_API_URL } from '../../../config';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { StyledButton } from '../buttons/StyledButton';
import {
  BhvkBackButton,
  BhvkSaveButton,
  BhvkTableActionButton,
  BhvkTableDeleteWithConfirmButton,
  BhvkTableEditButton,
} from '../buttons/ActionButtons';
import { httpClient } from '../../../utils/httpClient';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import ReferenceFieldNullable from '../../../components/fields/ReferenceFieldNullable';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { BooleanCheckBoxInput } from '../components/inputs/BooleanCheckBoxInput';
import { refreshContext } from '../../../utils/authProvider';
import { PhoneNumberInput } from '../../../components/inputs/PhoneNumberInput';
import { UsernameInput } from '../components/inputs/UsernameInput';
import { ResetPasswordButton } from '../../../components/buttons/ResetPasswordButtons';
import { RegistrationCodePicker } from '../../../components/RegistrationCodePicker';
import { getCurrentCustomer } from '../../../lib/currentCustomer';
import ReferenceArrayFieldNullable from '../../../components/fields/ReferenceArrayFieldNullable';
import { AvatarPictureField } from '../../../components/fields/AvatarPictureField';
import { TwoFactorButton } from '../../../components/buttons/TwoFactorButton';
import { AvatarImageInput } from '../../../components/inputs/AvatarImageInput';
import { GrayDateInputWithUpperLabel } from '../components/inputs/GrayDateInputWithUpperLabel';
import { GrayAutocompleteInputWithUpperLabel } from '../components/inputs/GrayAutocompleteInputWithUpperLabel';
import { GrayAutocompleteArrayInputWithUpperLabel } from '../components/inputs/GrayAutocompleteArrayInputWithUpperLabel';
import { CertificationExpiryWithDaysCaption } from '../../../components/CertificationExpiryWithDaysCaption';
import { PageContentCard } from '../cards/PageContentCard';
import { FormToolbar } from '../components/FormToolbar';
import { ExpiryDateDisplay } from '../components/ExpiryDateDisplay';
import {
  calculateDaysUntilDate, certificationExpiryChipColor, getCertificationExpiryText,
} from '../../../utils/certificationsAndItemsExpiry';
import { CertificationDocumentInput } from '../components/inputs/CertificationDocumentInput';
import { RenewalChoiceDialog } from '../components/RenewalChoiceDialog';
import { PlanRenewalFormValues, PlanRenewalModal } from '../components/PlanRenewalModal';
import { RenewNowFormValues, RenewNowModal } from '../components/RenewNowModal';
import DateFieldNullable from '../../../components/fields/DateFieldNullable';
import { getMinimumNextDate } from '../../../utils/timeGranularityUtils';
import { userCertificationTransformer } from '../../../utils/certifiedUsersAssignments';
import { NextEmailAlert } from '../components/NextEmailAlert';
import { appUsersResource } from './inventoryItems/util';
import { notifyWithError } from '../../../utils/errorHandling';
import { RaXgacRecord } from '../../../lib/RaXgacRecord';
import { StatusCounterCard } from '../components/StatusCounterCard';
import { notLaterThan, notLaterThanToday } from '../../../utils/dateValidators';
import TextFieldNullable from '../../../components/fields/TextFieldNullable';
import { DOCUMENT_ACCEPT } from '../../../utils/certificationDocuments';

const apiUrl = XGAC_MAIN_API_URL;

type AssetListRecord = RaXgacRecord<Asset> & {
  certificationCategories?: string[];
};

type CertificationCategoryListRecord = RaXgacRecord<CertificationCategory> & {
  largestReminderPeriodInDays: number;
};

const useCertificationsEnabled = () => {

  const currentCustomerId = getCurrentCustomer()?.value || '';
  const { data: customerRecord } = useGetOne(
    'customers',
    { id: currentCustomerId },
    { enabled: Boolean(currentCustomerId) },
  );
  return !!customerRecord?.preferences?.recurringTasksEnabled;

};

const Grid = Grid2;
const assetUserTransformer = (asset: any) => {

  return {
    ...asset,
    available: asset.available || false,
    username: asset.username || asset.properties.email,
    properties: {
      ...asset.properties,
      smsNumber: asset.properties?.phoneNumber,
      locale: asset.properties?.locale || 'nl',
    },
  };

};

const assetUserEditTransformer = (asset: any) => {

  if (asset.assetGroups) {

    asset.assetGroups = asset.assetGroups.map((group: any) => {

      return {
        _id: group,
        _ref: 'AssetGroup',
      };

    });

  }
  if (asset.properties?.image === null) {

    delete asset.properties.image;

  }
  return {
    ...asset,
    properties: {
      ...asset.properties,
      smsNumber: asset.properties?.phoneNumber,
      locale: asset.properties?.locale || 'nl',
    },
  };

};

const BooleanSetterField = (props: {
  label: string;
  source: string;
  lastUpdated: Date | null;
  setLastUpdated: Dispatch<SetStateAction<Date | null>>;
}) => {

  const refresh = useRefresh();
  const record = useRecordContext();
  const initialValue = get(record, props.source);
  const translate = useTranslate();
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {

    if (!record) {

      return;

    }
    if (props.source === 'isAdmin') {

      return;

    }
    setIsSaving(true);
    setValue(event.target.checked);
    const patchData = {
      [props.source]: event.target.checked,
    };
    await xgacDataProvider.update('assets', { id: record.id, data: patchData, previousData: record });
    setIsSaving(false);
    refresh();

  };
  useEffect(() => {

    const newValue = get(record, props.source);
    if (newValue !== value) {

      setValue(newValue);

    }
    if (record) {

      if (!props.lastUpdated) {

        props.setLastUpdated(new Date(record.updatedAt));
        return;

      }
      if (new Date(record.updatedAt) > props.lastUpdated) {

        props.setLastUpdated(new Date(record.updatedAt));

      }

    }

  }, [record]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isSaving) {

    return (
      <>
        <CircularProgress size={20}/>
      </>
    );

  }

  if (props.source === 'available' && !record?.properties.phoneNumber) {

    return (
      <>
        <Checkbox checked={false} sx={{ ml: -1.5 }} disabled={true} />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <BhvkTableEditButton label="resources.app-users.text.fill_phonenumber" resource="assets"/>
      </>
    );

  }

  return (
    <>
      <Checkbox
        checked={value}
        sx={{ ml: -1.5 }}
        onChange={handleChange}
        onClick={(event) => event.stopPropagation()}
      />
      <span>{translate(value ? 'general.text.yes' : 'general.text.no')}</span>
    </>
  );

};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IsAdminBooleanSetterField = (props: FieldProps & { changeHandler?: any }) => {

  const record = useRecordContext();
  const refresh = useRefresh();
  const [relation, setRelation] = useState<CustomerUserRelation | null>(null);
  const [value, setValue] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const loggedInUserId = useGetIdentity().data?.id;
  const disabled = !record || !record.user?._id || record.user?._id === loggedInUserId;
  const translate = useTranslate();

  useEffect(() => {

    if (!relation && record?.user?._id && !disabled && record.customer?._id) {

      const getRelation = async () => {

        setIsSaving(true);

        const relationRequest = await httpClient(`${apiUrl}/customer-user-relations?${queryString.stringify({
          'user._id': record.user._id,
          'customer._id': record.customer._id,
        })}`);

        setRelation(relationRequest.json?.result[0] || null);
        const rolesHasBhvkOrAdmin = ['bhvk_admin', 'bhvk_trial'].some((role: string) => relationRequest.json?.result[0]?.roles.includes(role));
        setValue(rolesHasBhvkOrAdmin);
        setIsSaving(false);

      };
      getRelation();

    }

  }, [disabled, record, relation]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {

    setIsSaving(true);
    if (!relation) {

      setIsSaving(false);
      return;

    }
    setValue(event.target.checked);
    const patchData = {
      roles: event.target.checked ? [...relation.roles, 'bhvk_admin'] : relation.roles.filter((role: string) => !['bhvk_admin', 'bhvk_trial'].includes(role)),
    };
    const newRelation = await xgacDataProvider.update('customer-user-relations', { id: relation._id, data: patchData, previousData: relation });

    if (event.target.checked) {

      await httpClient(`${apiUrl}/users/reset-password/${relation.user._id}?bhvk=true&admin_role_assigned=true`);

    }
    setRelation(newRelation.data);
    setIsSaving(false);
    refresh();

    if (props.changeHandler) {

      props.changeHandler();

    }

  };

  if (isSaving) {

    return (
      <>
        <CircularProgress size={20}/>
      </>
    );

  }

  return (
    <>
      <Checkbox
        checked={value}
        onChange={handleChange}
        sx={{ ml: -1.5 }}
        disabled={disabled}
        onClick={(event) => event.stopPropagation()}
      />
      <span>{translate(value ? 'general.text.yes' : 'general.text.no')}</span>
    </>
  );

};

const AssetActions = () => {

  const redirect = useRedirect();
  const translate = useTranslate();
  return (
    <>
      <StyledButton
        onClick={() => {

          redirect('/help/de-app/hoe-installeer-je-de-bhv-knop-nl-app/');

        }}
        label="general.text.install_app"
        sx={{
          whiteSpace: 'nowrap', paddingLeft: '30px', paddingRight: '30px', marginBottom: '5px', marginRight: '10px',
        }}
        color="primary"
      />
      <>
        <MuiButton
          variant={'contained'}
          color={'secondary'}
          size={'small'}
          component={Link}
          to={{
            pathname: '/assets/create',
          }}
          sx={{
            whiteSpace: 'nowrap', paddingLeft: '30px', paddingRight: '30px', marginBottom: '5px',
          }}
          disabled={false}
        >
          {translate('resources.assets.text.add_user')}
        </MuiButton>
      </>
    </>
  );

};

const AssetCounters = (props: {
  lastUpdated: Date | null;
  customerId: string;
  certificationsEnabled: boolean;
}) => {

  const translate = useTranslate();

  const [availableCount, setAvailableCount] = useState(0);
  const [lastSavedCustomer, setLastSavedCustomer] = useState<string | null>(null);
  const identity = useGetIdentity();
  const currentCustomer = getCurrentCustomer()?.value;

  const { data: certificationCounts = { expiring: 0, expired: 0 } } = useQuery<{ expiring: number; expired: number }>({
    queryKey: ['assets', 'certification-expiry-counts', props.customerId],
    queryFn: async () => {

      const { json }: { json: { expiring: number; expired: number } } = await xgacDataProvider.getCertificationExpiryCounts(props.customerId);
      return json;

    },
    enabled: props.certificationsEnabled && Boolean(props.customerId),
  });

  useEffect(() => {

    if (currentCustomer && currentCustomer !== lastSavedCustomer) {

      setLastSavedCustomer(currentCustomer);

    }

  }, [identity, currentCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    xgacDataProvider.getList(
      'all-assets',
      {
        pagination: { page: 1, perPage: 1 },
        filter: { available: true, 'user._ref': 'User', 'properties.phoneNumber': '!null' },
        sort: { field: 'name', order: 'ASC' },
      },
    ).then((response) => {

      setAvailableCount(response.total || 0);

    });

  }, [lastSavedCustomer, props.lastUpdated]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
    <StatusCounterCard
      variant="info"
      count={availableCount}
      caption={translate('resources.assets.text.number_of_available_users')}
      countLabel={translate(
        availableCount === 1 ? 'resources.assets.text.user_count' : 'resources.assets.text.users_count',
        { count: availableCount },
      )}
    />
    {props.certificationsEnabled && (
      <>
        <StatusCounterCard
          variant="expiring"
          count={certificationCounts.expiring}
          caption={translate('resources.assets.text.number_of_expiring_certifications')}
          countLabel={translate(
            certificationCounts.expiring === 1
              ? 'resources.assets.text.certificate_count'
              : 'resources.assets.text.certificates_count',
            { count: certificationCounts.expiring },
          )}
        />
        <StatusCounterCard
          variant="expired"
          count={certificationCounts.expired}
          caption={translate('resources.assets.text.number_of_expired_certifications')}
          countLabel={translate(
            certificationCounts.expired === 1
              ? 'resources.assets.text.certificate_count'
              : 'resources.assets.text.certificates_count',
            { count: certificationCounts.expired },
          )}
        />
      </>
    )}
  </div>;

};

const AssetHasAppField = (props: FieldProps) => {

  const record = useRecordContext();
  if (!record) {

    return null;

  }
  const lastObservationAt = record.lastObservationAt;
  if (!lastObservationAt) {

    return <BooleanField record={{ source: false }} source="source" label={props.label} />;

  }
  const onlineThreshold = moment(record.lastObservationAt).add(1, 'week');

  const isApp = moment.utc().isBefore(onlineThreshold);
  return <BooleanField record={{ source: isApp }} source="source" label={props.label} />;

};

const getExpiringDaysByCategoryId = (asset: Asset) => {

  const result: Record<string, number> = {};
  for (const certification of asset.certifications ?? []) {

    const categoryId = certification.certificationCategory?._id;
    if (!categoryId || !certification.expiryDate) continue;
    result[categoryId] = calculateDaysUntilDate(certification.expiryDate);

  }
  return result;

};

const getCertificationsByCategoryId = (asset: Asset) => {

  const result = new Map<string, NonNullable<Asset['certifications']>[number]>();
  for (const certification of asset.certifications ?? []) {

    const categoryId = certification.certificationCategory?._id;
    if (!categoryId) continue;
    result.set(categoryId, certification);

  }
  return result;

};

// disabled because the label is simply added to display the column name in the datagrid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CertificationCategoriesCell = (props: {
  categoriesById: Map<string, CertificationCategoryListRecord>;
  categoriesLoading: boolean;
  label: string;
}) => {

  const translate = useTranslate();
  const asset = useRecordContext<AssetListRecord>();
  const categoryIds = asset?.certificationCategories ?? [];

  if (!asset) {

    return null;

  }

  if (categoryIds.length === 0) {

    return <TextField source="value" record={{ value: translate('general.text.none') }} className="gray" />;

  }

  if (props.categoriesLoading) {

    return <CircularProgress size={16} />;

  }

  const categories = categoryIds
    .map((id) => props.categoriesById.get(String(id)))
    .filter((category): category is CertificationCategoryListRecord => Boolean(category));

  const expiringDaysByCategoryId = getExpiringDaysByCategoryId(asset);
  const certificationsByCategoryId = getCertificationsByCategoryId(asset);

  let hasIssue = false;
  const tooltipLines: {
    id: string;
    name: string;
    expiryText: string;
    expiryChipColor: 'default' | 'warning' | 'error';
    missingProof: boolean;
    plannedRenewalLabel?: string;
  }[] = [];

  for (const category of categories) {

    const categoryId = String(category.id ?? category._id);
    if (!categoryId) continue;

    const days = expiringDaysByCategoryId[categoryId];
    if (days === undefined) continue;

    const certification = certificationsByCategoryId.get(categoryId);
    const missingProof = Boolean(
      category.isDocumentaryProofRequired && !certification?.documentaryProof,
    );

    const plannedRenewalDate = certification?.plannedRenewalDate ?? null;
    const hasActivePlannedRenewal = Boolean(
      plannedRenewalDate
      && calculateDaysUntilDate(plannedRenewalDate) >= 0,
    );

    const rawExpiryChipColor = certificationExpiryChipColor(
      days,
      category.largestReminderPeriodInDays ?? 0,
    );
    const isExpired = rawExpiryChipColor === 'error';
    const isExpiring = rawExpiryChipColor === 'warning';

    if (missingProof || (!hasActivePlannedRenewal && (isExpired || isExpiring))) {

      hasIssue = true;

    }

    const highlightIssues = !hasActivePlannedRenewal;
    let expiryChipColor: 'default' | 'warning' | 'error' = 'default';
    if (highlightIssues && isExpired) {

      expiryChipColor = 'error';

    } else if (highlightIssues && isExpiring) {

      expiryChipColor = 'warning';

    }

    tooltipLines.push({
      id: categoryId,
      name: category.name ?? '',
      expiryText: getCertificationExpiryText(days, translate, false),
      expiryChipColor,
      missingProof,
      plannedRenewalLabel: hasActivePlannedRenewal
        ? translate('resources.assets.certifications.text.planned_renewal_in_tooltip', {
          date: moment(plannedRenewalDate).format('DD/MM/YYYY'),
        })
        : undefined,
    });

  }

  tooltipLines.sort((a, b) => {

    if (a.expiryChipColor !== b.expiryChipColor) {

      if (a.expiryChipColor === 'error') return -1;
      if (b.expiryChipColor === 'error') return 1;
      if (a.expiryChipColor === 'warning') return -1;
      return 1;

    }

    if (a.missingProof !== b.missingProof) {

      return a.missingProof ? -1 : 1;

    }

    return 0;

  });

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 420,
            p: 1,
          },
        },
      }}
      title={(
        <Table
          size="small"
          sx={{
            minWidth: 320,
            '& .MuiTableCell-root': {
              color: 'common.white',
              px: 1,
              py: 0.75,
              verticalAlign: 'top',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                {translate('general.fields.name')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {translate('resources.assets.certifications.text.days_until_expiry_column')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tooltipLines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>
                  <Stack spacing={0.5} alignItems="flex-start">
                    <Typography
                      variant="body2"
                      sx={{ color: 'common.white', fontWeight: 600 }}
                    >
                      {line.name}
                    </Typography>
                    {line.missingProof && (
                      <Chip
                        size="small"
                        color="warning"
                        label={translate(
                          'resources.assets.certifications.text.missing_documentary_proof',
                        )}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5} alignItems="flex-start">
                    <Chip
                      size="small"
                      label={line.expiryText}
                      color={line.expiryChipColor}
                      sx={line.expiryChipColor === 'default' ? {
                        bgcolor: 'grey.700',
                        color: 'common.white',
                      } : undefined}
                    />
                    {line.plannedRenewalLabel && (
                      <Chip
                        size="small"
                        label={line.plannedRenewalLabel}
                        sx={{
                          bgcolor: 'grey.700',
                          color: 'common.white',
                        }}
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    >
      <span style={{ display: 'inline-flex' }}>
        {tooltipLines.length > 1 && (
          <Stack direction="row" spacing={0.5}>
            {hasIssue ? (
              <>
                <WarningAmberOutlinedIcon color="error" fontSize="small" />
                <span style={{ fontWeight: 600 }}>{tooltipLines.length}</span>
              </>
            ) : (
              <>
                <VerifiedIcon color="success" fontSize="small" />
                <span style={{ fontWeight: 600 }}>{tooltipLines.length}</span>
              </>
            )}
          </Stack>
        )}
        {tooltipLines.length === 1 && (
          hasIssue ? (
            <WarningAmberOutlinedIcon color="error" fontSize="small" />
          ) : (
            <VerifiedIcon color="success" fontSize="small" />
          )
        )}

      </span>
    </Tooltip>
  );

};

const AssetBulkActionButtons = (props: {
  commonCategories: CertificationCategoryListRecord[];
  selectedAssets: AssetListRecord[];
  categoriesLoading: boolean;
  onRenew: (category: CertificationCategoryListRecord, selectedAssets: AssetListRecord[]) => void;
}) => {

  const translate = useTranslate();

  if (props.categoriesLoading) return null;

  const emptyStateMessage = props.selectedAssets.length === 1 && props.commonCategories.length === 0
    ? translate('resources.assets.certifications.text.selected_user_no_certificates')
    : translate('resources.assets.certifications.text.no_common_certificates');

  return (
    props.commonCategories.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyStateMessage}
      </Typography>
    ) : (
      <Box
        sx={{
          display: 'grid',
          width: 'max-content',
          gridTemplateColumns: `repeat(${Math.max(1, Math.min(3, props.commonCategories.length))}, 1fr)`,
          gap: 1,
        }}
      >
        {props.commonCategories.map((category) => (
          <Button
            key={category.id}
            variant="contained"
            label={`${translate('resources.assets.certifications.text.renew')}: ${category.name}`}
            startIcon={<LoopIcon />}
            onClick={() => props.onRenew(category, props.selectedAssets)}
          />
        ))}
      </Box>
    )
  );

};

const AssetDatagridRow = (props: DatagridRowProps) => {

  const translate = useTranslate();

  return (
    <Tooltip
      title={props.selectable ? null : translate('resources.assets.certifications.text.no_common_category_tooltip')}
      arrow
      followCursor
      placement="top"
    >
      <DatagridRow {...props} />
    </Tooltip>
  );

};

const AssetDatagrid = (props: {
  hasGroupsField: boolean;
  certificationsEnabled: boolean;
  lastUpdated: Date | null;
  setLastUpdated: Dispatch<SetStateAction<Date | null>>;
  onRenew: (category: CertificationCategoryListRecord, selectedAssets: Asset[]) => void;
}) => {

  // assets are only from the current page, selectedIds are from the entire list
  const { data: assets = [], selectedIds } = useListContext<AssetListRecord>();
  const [retainedAssetsById, setRetainedAssetsById] = useState(() => new Map<string, AssetListRecord>());

  useEffect(() => {

    setRetainedAssetsById((previous) => {

      const next = new Map<string, AssetListRecord>();
      for (const selectedId of selectedIds) {

        const retainedAsset = previous.get(String(selectedId));
        if (retainedAsset) {

          next.set(String(selectedId), retainedAsset);

        }

      }
      for (const asset of assets) {

        next.set(String(asset.id), asset);

      }
      return next;

    });

  }, [assets, selectedIds]);

  const selectedAssets = useMemo(() => {

    return selectedIds
      .map((id) => retainedAssetsById.get(String(id)))
      .filter((asset): asset is AssetListRecord => Boolean(asset));

  }, [retainedAssetsById, selectedIds]);

  const commonCategoryIds = useMemo(() => {

    if (selectedAssets.length === 0 || selectedAssets.length !== selectedIds.length) {

      return new Set<string>();

    }

    const categorySets = selectedAssets.map(
      (asset) => new Set((asset.certificationCategories ?? []).map(String)),
    );
    return categorySets.reduce((common, categories) => (
      new Set([...common].filter((id) => categories.has(id)))
    ));

  }, [selectedAssets, selectedIds.length]);

  const categoryIds = useMemo(() => {

    const ids = new Set<string>();
    for (const asset of [...assets, ...selectedAssets]) {

      for (const categoryId of asset.certificationCategories ?? []) {

        ids.add(String(categoryId));

      }

    }

    return Array.from(ids).sort();

  }, [assets, selectedAssets]);

  const { data: categories = [], isLoading: categoriesLoading } = useGetMany<CertificationCategoryListRecord>(
    'certificates',
    { ids: categoryIds },
    { enabled: categoryIds.length > 0 },
  );

  const categoriesById = useMemo(() => {

    return new Map(
      categories.map((category) => [String(category.id ?? category._id), category]),
    );

  }, [categories]);

  const isRowSelectable = useCallback((asset: AssetListRecord) => {

    if (selectedIds.length === 0 || selectedIds.includes(asset.id)) {

      return true;

    }
    return (asset.certificationCategories ?? []).some((categoryId) => commonCategoryIds.has(categoryId));

  }, [commonCategoryIds, selectedIds]);

  const commonCategories = Array.from(commonCategoryIds)
    .map((categoryId) => categoriesById.get(categoryId))
    .filter((category): category is CertificationCategoryListRecord => Boolean(category));
  const bulkActionRows = Math.max(1, Math.ceil(commonCategories.length / 3));

  return (
    <Datagrid
      sx={{
        '& .RaBulkActionsToolbar-toolbar': {
          alignItems: 'center',
          gap: 1,
          overflow: 'hidden',
        },
        '& .RaBulkActionsToolbar-toolbar:not(.RaBulkActionsToolbar-collapsed)': {
          height: (theme) => theme.spacing(6 * bulkActionRows),
        },
        '& .RaBulkActionsToolbar-topToolbar': {
          flex: '1 1 auto',
          justifyContent: 'flex-end',
          alignItems: 'center',
          alignSelf: 'center',
          minWidth: 0,
          padding: 0,
          paddingBottom: 0,
        },
      }}
      body={<DatagridBody row={<AssetDatagridRow />} />}
      isRowSelectable={isRowSelectable}
      rowSx={(asset) => ({
        '& > .MuiTableCell-root': {
          color: isRowSelectable(asset) ? 'text.primary' : 'text.disabled',
        },
      })}
      rowClick="toggleSelection"
      bulkActionButtons={
        <AssetBulkActionButtons
          commonCategories={commonCategories}
          selectedAssets={selectedAssets}
          categoriesLoading={categoriesLoading}
          onRenew={props.onRenew}
        />
      }
      resource="assets"
    >
      <AvatarPictureField/>
      <TextField source="name" label="general.fields.name"/>
      <ReferenceFieldNullable source="user._id" reference="users" label="resources.users.fields.username">
        <TextFieldNullable source="username" privacy />
      </ReferenceFieldNullable>
      {props.hasGroupsField && (
        <ReferenceArrayFieldNullable source="assetGroups" reference="asset-groups" label="resources.asset-groups.name"/>
      )}
      {props.certificationsEnabled && (
        <CertificationCategoriesCell
          label="resources.users.fields.certification_categories"
          categoriesById={categoriesById}
          categoriesLoading={categoriesLoading}
        />
      )}
      <IsAdminBooleanSetterField source={''} label="resources.users.text.access_to_dashboard"/>
      <BooleanSetterField
        label="resources.users.text.receives_alarms"
        source="available"
        lastUpdated={props.lastUpdated}
        setLastUpdated={props.setLastUpdated}
      />
      <AssetHasAppField source="lastObservationAt" label="resources.assets.fields.has_app"/>
      <BhvkTableEditButton resource="assets" label="general.text.more_settings" />
    </Datagrid>
  );

};

export const AssetList = () => {

  const translate = useTranslate();
  const certificationsEnabled = useCertificationsEnabled();
  const unselectAll = useUnselectAll('all-assets');
  const notify = useNotify();
  const refresh = useRefresh();

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [lastSavedCustomer, setLastSavedCustomer] = useState<string | null>(null);
  const [openDialogStep, setOpenDialogStep] = useState<'renewalChoice' | 'renewNow' | 'planRenewal' | 'closed'>('closed');
  const [selectedCategory, setSelectedCategory] = useState<CertificationCategoryListRecord | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [renewFlowSessionKey, setRenewFlowSessionKey] = useState(0);
  const currentCustomer = getCurrentCustomer()?.value;
  const identity = useGetIdentity();

  const { data } = useGetList('app-users', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
    meta: {
      $select: 'assetGroups',
    },
  });

  const bulkRenewRecipients = useMemo(
    () => selectedAssets.map((asset) => ({
      id: asset._id,
      label: asset.name,
    })),
    [selectedAssets],
  );

  const closeRenewFlow = useCallback(() => {

    setOpenDialogStep('closed');
    setRenewFlowSessionKey((k) => k + 1);

  }, []);

  useEffect(() => {

    if (currentCustomer && currentCustomer !== lastSavedCustomer) {

      setLastSavedCustomer(currentCustomer);
      setLastUpdated(null);

    }

  }, [identity, currentCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  // unselecting all on component unmount
  useEffect(() => () => unselectAll(), [unselectAll]);

  const hasGroupsField = useMemo(() => {

    if (!data) {

      return false;

    }
    const groups = data?.map((asset: { assetGroups: string[] }) => asset.assetGroups);
    return !groups.every((group: string[]) => {

      return _.isEqual(group, groups[0]);

    });

  }, [data]);

  const updateSelectedAssetCertifications = useCallback(async (
    getUpdateData: (asset: Asset) => Partial<AssetCertification>,
  ) => {

    if (selectedAssets.length === 0) return;
    await Promise.all(
      selectedAssets.map((asset) => {

        const certification = asset.certifications?.find(
          (c) => c.certificationCategory._id === selectedCategory?.id,
        );
        if (!certification?._id) return Promise.resolve();
        return xgacDataProvider.update('user-certifications', {
          id: certification._id,
          data: getUpdateData(asset),
          previousData: certification,
          meta: {
            assetId: asset._id,
            certificationId: certification._id,
          },
        });

      }),
    );

  }, [selectedAssets, selectedCategory]);

  const handleBulkRenew = useCallback(async (values: PlanRenewalFormValues) => {

    try {

      await updateSelectedAssetCertifications(() => ({ plannedRenewalDate: new Date(values.plannedRenewalDate).toISOString() }));
      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: selectedAssets.length },
      });
      refresh();
      setSelectedAssets([]);
      unselectAll();
      closeRenewFlow();

    } catch (error) {

      notifyWithError(notify, error);

    }

  }, [updateSelectedAssetCertifications, notify, refresh, selectedAssets, unselectAll, closeRenewFlow]);

  const handleBulkRenewNow = useCallback(async (values: RenewNowFormValues) => {

    if (!('certificationsByAssetId' in values)) return;

    const missingAssetIds = selectedAssets
      .filter((asset) => !values.certificationsByAssetId[asset._id])
      .map((asset) => asset._id);

    if (missingAssetIds.length > 0) {

      throw new Error(
        `Missing certification details for asset(s): ${missingAssetIds.join(', ')}`,
      );

    }

    try {

      await updateSelectedAssetCertifications((asset) => {

        const details = values.certificationsByAssetId[asset._id];

        return {
          lastReceivedOn: new Date(details.lastReceivedOn).toISOString(),
          expiryDate: new Date(details.expiryDate).toISOString(),
          ...(details.document?.rawFile && { document: details.document }),
        };

      });

      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: selectedAssets.length },
      });
      refresh();
      setSelectedAssets([]);
      unselectAll();
      closeRenewFlow();

    } catch (error) {

      notifyWithError(notify, error);

    }

  }, [updateSelectedAssetCertifications, notify, refresh, selectedAssets, unselectAll, closeRenewFlow]);

  return (
    <PageContentCard>
      <h3>{translate('resources.users.text.title')}</h3>
      <p>{translate('resources.users.text.sub_title')}</p>
      {currentCustomer ? (
        <AssetCounters
          lastUpdated={lastUpdated}
          customerId={currentCustomer}
          certificationsEnabled={certificationsEnabled}
        />
      ) : null}
      <ListLive actions={<AssetActions/>}
        filters={SearchFields}
        debounce={LIST_DEBOUNCE}
        title="resources.assets.text.without_app_title"
        resource="all-assets"
        filter={{ 'user._ref': 'User', type: '!kiosk' }}
        empty={false}
        sx={{ '& > .RaList-main > .RaList-content': { mt: '12px' } }}
      >
        <AssetDatagrid
          hasGroupsField={hasGroupsField}
          certificationsEnabled={certificationsEnabled}
          lastUpdated={lastUpdated}
          setLastUpdated={setLastUpdated}
          onRenew={(category: CertificationCategoryListRecord, selectedAssetsProp: Asset[]) => {

            setSelectedCategory(category);
            setSelectedAssets(selectedAssetsProp);
            setRenewFlowSessionKey((k) => k + 1);
            setOpenDialogStep('renewalChoice');

          }}
        />
      </ListLive>
      <RenewalChoiceDialog
        variant="certification"
        open={openDialogStep === 'renewalChoice'}
        onClose={closeRenewFlow}
        onRenewNow={() => {

          setOpenDialogStep('renewNow');

        }}
        onPlanRenewal={() => {

          setOpenDialogStep('planRenewal');

        }}
      />
      <RenewNowModal
        formSessionKey={renewFlowSessionKey}
        open={openDialogStep === 'renewNow'}
        onClose={closeRenewFlow}
        onBack={() => setOpenDialogStep('renewalChoice')}
        onSubmit={handleBulkRenewNow}
        startDateLabel="resources.assets.certifications.fields.last_received_on"
        expiryDateLabel="resources.assets.certifications.fields.expiry_date"
        helperText={translate('resources.assets.certifications.text.expiry_date_input_helper_text')}
        title="resources.assets.certifications.text.renew_now_modal_title"
        timeGranularity={selectedCategory?.validityPeriod}
        hasDocumentaryProofField={selectedCategory?.isDocumentaryProofRequired}
        bulkRecipients={bulkRenewRecipients}
        startDateSource="lastReceivedOn"
        expiryDateSource="expiryDate"
      />
      <PlanRenewalModal
        formSessionKey={renewFlowSessionKey}
        open={openDialogStep === 'planRenewal'}
        onClose={closeRenewFlow}
        onBack={() => {

          setOpenDialogStep('renewalChoice');

        }}
        onSubmit={handleBulkRenew}
        dateFieldSource="plannedRenewalDate"
        dateFieldLabel="resources.assets.certifications.fields.planned_renewal_date"
        dateFieldHelperText="resources.assets.certifications.text.planned_renewal_date_helper_text"
        title="resources.assets.certifications.text.plan_renewal_modal_title"
      />
    </PageContentCard>
  );

};

const ResetButtonPreparer = (props: { updatedAt: Date | null }) => {

  const record = useRecordContext();
  const [user, setUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  useEffect(() => {

    if (!user && record?.user?._id) {

      xgacDataProvider.getOne('users', { id: record.user?._id }).then((response) => {

        setUser(response.data as unknown as User);

      });

    }

  }, [record?.user?._id, user]);
  useEffect(() => {

    if (!record || !record.user?._id) {

      return;

    }
    httpClient(`${apiUrl}/customer-user-relations?${queryString.stringify({ 'user._id': record.user._id })}`).then((response) => {

      if (response?.json.result[0]?.roles) {

        setUserRoles(response.json.result[0].roles);

      }

    });

  }, [record, props.updatedAt]);

  return (
    <>
      {(
        userRoles.includes('admin')
        || userRoles.includes('support_admin')
        || userRoles.includes('sales_admin')
        || userRoles.includes('bhvk_admin')
        || userRoles.includes('bhvk_trial')) ? (
          <ResetPasswordButton user={user} disabled={false} bhvk={true}/>
        ) : (
          <ResetPasswordButton user={user} disabled={true} bhvk={true}/>
        )}

    </>
  );

};

const AssetDeleteButton = () => {

  const notify = useNotify();
  const redirect = useRedirect();
  const loggedInUser = useGetIdentity().data?.id;
  const record = useRecordContext();
  const translate = useTranslate();
  const customMessage = `${translate('ra.action.delete')} ${record?.name}`;

  const onSuccess = () => {

    notify('resources.assets.text.bhvk_deleted');
    redirect('/assets');

  };

  return (
    <DeleteWithConfirmButton
      icon={<></>}
      variant="contained"
      color="inherit"
      resource="app-users"
      confirmTitle={customMessage}
      mutationOptions={{ onSuccess }}
      label={'resources.assets.text.delete_user'}
      className="BhvkCancelButton-root"
      sx={{ height: 'fit-content' }}
      size="medium"
      disabled={!record || record.user?._id === loggedInUser}
    />
  );

};

type AssetCertification = NonNullable<Asset['certifications']>[number];

const CertificationListRowActions = (props: {
  assetRecord: Asset;
  onRenew: (certification: AssetCertification & { certificationCategory?: CertificationCategory }) => void;
 }) => {

  const refresh = useRefresh();
  const translate = useTranslate();

  const certificationRecord = useRecordContext<AssetCertification & { certificationCategory?: CertificationCategory }>();
  if (!certificationRecord) {

    return null;

  }

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <BhvkTableEditButton
        resource="user-certifications"
        to={`/assets/${props.assetRecord._id}/certificates/${certificationRecord._id}`}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // property is present according to the documentation (see below). works correctly at runtime
        // https://marmelab.com/react-admin/doc/5.6/Edit.html#prefilling-the-form
        state={{
          record: { ...certificationRecord, id: certificationRecord._id },
          assetName: props.assetRecord.name,
        }}
      />
      <BhvkTableActionButton
        label="resources.assets.certifications.text.renew"
        onClick={() => {

          props.onRenew(certificationRecord);

        }}
      />
      <BhvkTableDeleteWithConfirmButton
        resource="user-certifications"
        redirect={false}
        record={{ ...certificationRecord, id: certificationRecord._id }}
        mutationOptions={{
          meta: { assetId: props.assetRecord._id },
          onSettled: () => refresh(),
        }}
        successMessage={'resources.assets.certifications.text.certification_deleted'}
        confirmTitle={`${translate('ra.action.delete')} ${translate('resources.assets.certifications.text.title_single').toLowerCase()}`}
      />
    </Stack>
  );

};

const CertificationExpiryField = ({
  categoriesById,
  label,
}: {
  categoriesById: Map<string, { largestReminderPeriodInDays: number }
  >;
  label?: string;
}) => {

  const certification = useRecordContext<CertificationProperties & { id?: string }>();
  if (!certification?.expiryDate) return null;

  const categoryId = certification.certificationCategory?._id;
  const largestReminderPeriodInDays = categoriesById.get(categoryId ?? '')?.largestReminderPeriodInDays ?? 0;

  return (
    <CertificationExpiryWithDaysCaption
      label={label}
      record={{
        ...certification,
        id: certification.id ?? categoryId,
        largestReminderPeriodInDays,
      }}
    />
  );

};

const CertificationList = () => {

  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const assetRecord = useRecordContext<Asset>();
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewDialogChoice, setRenewDialogChoice] = useState<'renew_now' | 'plan_renewal' | 'closed'>('closed');
  const [renewingCertification, setRenewingCertification] = useState<AssetCertification & { certificationCategory?: CertificationCategory } | null>(null);
  const [renewFlowSessionKey, setRenewFlowSessionKey] = useState(0);

  const userCertificationCategoryIds = assetRecord?.certifications?.map((certification) => certification.certificationCategory?._id);

  const { data: userCertificationCategories = [] } = useGetMany('certificates', {
    ids: userCertificationCategoryIds ?? [],
  });

  const hasAtLeastOneDocumentaryProof = userCertificationCategories?.some((category) => category.isDocumentaryProofRequired);

  const categoriesById = useMemo(() => new Map(userCertificationCategories.map((category) => [category.id, category])), [userCertificationCategories]);

  if (!assetRecord) {

    return null;

  }

  const closeRenewDialog = () => {

    setRenewDialogOpen(false);
    setRenewDialogChoice('closed');
    setRenewingCertification(null);
    setRenewFlowSessionKey((k) => k + 1);

  };

  const handleRenewNowSubmit = async (values: RenewNowFormValues) => {

    if (!renewingCertification?._id || !assetRecord._id || !('lastReceivedOn' in values)) return;

    const data = {
      lastReceivedOn: new Date(values.lastReceivedOn).toISOString(),
      expiryDate: new Date(values.expiryDate).toISOString(),
      ...(values.document?.rawFile && { document: values.document }),
    };

    try {

      await xgacDataProvider.update('user-certifications', {
        id: renewingCertification._id,
        data,
        previousData: renewingCertification,
        meta: {
          assetId: assetRecord._id,
          certificationId: renewingCertification._id,
        },
      });
      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: 1 },
      });
      refresh();

    } catch (error) {

      notifyWithError(notify, error);

    }
    closeRenewDialog();

  };

  const handlePlanRenewalSubmit = async (values: {
    plannedRenewalDate: string;
  }) => {

    const data = {
      plannedRenewalDate: new Date(values.plannedRenewalDate).toISOString(),
    };

    if (!renewingCertification?._id || !assetRecord._id) return;

    try {

      await xgacDataProvider.update('user-certifications', {
        id: renewingCertification._id,
        data,
        previousData: renewingCertification,
        meta: {
          assetId: assetRecord._id,
          certificationId: renewingCertification._id,
        },
      });
      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: 1 },
      });
      refresh();

    } catch (error) {

      notifyWithError(notify, error);

    }
    closeRenewDialog();

  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}
      >
        <Typography variant="h6">
          {translate('resources.assets.certifications.text.sub_title')}
        </Typography>
        <CreateButton
          resource="user-certifications"
          label="resources.assets.certifications.text.add_certification"
          to={`/assets/${(assetRecord._id)}/certificates/create`}
          size="small"
          variant="contained"
        />
      </Stack>
      <ArrayField source="certifications">
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          sx={{ width: '100%' }}
        >
          <ReferenceField source="certificationCategory._id" reference="certificates" link={false} label="general.fields.name" />
          {hasAtLeastOneDocumentaryProof && (
            <DocumentaryProofField source="certificationCategory._id" label="resources.assets.certifications.fields.documentary_proof" />
          )}
          <DateField source="firstIssuedOn" label="resources.assets.certifications.fields.first_issued_on" />
          <DateField source="lastReceivedOn" label="resources.assets.certifications.fields.last_received_on" />
          <CertificationExpiryField categoriesById={categoriesById} label="resources.assets.certifications.fields.expiry_date" />
          <DateFieldNullable
            source="plannedRenewalDate"
            label="resources.assets.certifications.fields.planned_renewal_date"
            neverText="general.text.none"
          />
          <CertificationListRowActions
            assetRecord={assetRecord}
            onRenew={(certification: AssetCertification & { certificationCategory?: CertificationCategory }) => {

              setRenewingCertification(certification);
              setRenewFlowSessionKey((k) => k + 1);
              setRenewDialogOpen(true);

            }}
          />
        </Datagrid>
        <RenewalChoiceDialog
          variant="certification"
          open={renewDialogOpen}
          onClose={closeRenewDialog}
          onRenewNow={() => {

            setRenewDialogChoice('renew_now');
            setRenewDialogOpen(false);

          }}
          onPlanRenewal={() => {

            setRenewDialogChoice('plan_renewal');
            setRenewDialogOpen(false);

          }}
        />
        <RenewNowModal
          formSessionKey={renewFlowSessionKey}
          open={renewDialogChoice === 'renew_now'}
          onClose={closeRenewDialog}
          onBack={() => {

            setRenewDialogChoice('closed');
            setRenewDialogOpen(true);

          }}
          hasDocumentaryProofField={categoriesById.get(renewingCertification?.certificationCategory?._id ?? '')?.isDocumentaryProofRequired}
          timeGranularity={categoriesById.get(renewingCertification?.certificationCategory?._id ?? '')?.validityPeriod}
          startDateLabel="resources.assets.certifications.fields.last_received_on"
          expiryDateLabel="resources.assets.certifications.fields.expiry_date"
          helperText="resources.assets.certifications.text.renew_now_modal_helper_text"
          title="resources.assets.certifications.text.renew_now_modal_title"
          onSubmit={handleRenewNowSubmit}
          startDateSource="lastReceivedOn"
          expiryDateSource="expiryDate"
          defaultStartDate={renewingCertification?.lastReceivedOn}
          defaultExpiryDate={renewingCertification?.expiryDate}
        />
        <PlanRenewalModal
          formSessionKey={renewFlowSessionKey}
          open={renewDialogChoice === 'plan_renewal'}
          onClose={closeRenewDialog}
          onBack={() => {

            setRenewDialogChoice('closed');
            setRenewDialogOpen(true);

          }}
          onSubmit={handlePlanRenewalSubmit}
          dateFieldLabel="resources.assets.certifications.fields.planned_renewal_date"
          dateFieldHelperText="resources.assets.certifications.text.planned_renewal_date_helper_text"
          defaultPlannedRenewalDate={renewingCertification?.plannedRenewalDate}
          title="resources.assets.certifications.text.plan_renewal_modal_title"
        />
      </ArrayField>
    </>
  );

};

const AssetEditForm = (editProps: { setNeedsContextRefresh: SetStateAction<any> }) => {

  const record = useRecordContext();
  const certificationsEnabled = useCertificationsEnabled();
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [userLastLoggedIn, setUserLastLoggedIn] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const identity = useGetIdentity();
  const locale = useLocaleState();
  const currentUserId = identity.data?.id;
  const translate = useTranslate();
  const groupsRequest = useGetList('asset-groups', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
    meta: {
      $select: 'name',
    },
  });
  useEffect(() => {

    if (identity.data?.id === record?.user?._id) {

      editProps.setNeedsContextRefresh(true);

    }

  }, [identity, editProps, record]);
  useEffect(() => {

    if (record?.user?._id && !userLastLoggedIn) {

      xgacDataProvider.getOne('users', { id: record.user?._id }).then((response) => {

        const lastLoggedIn = response.data?.lastActiveAt;
        setUserName(response.data?.username);
        if (lastLoggedIn) {

          const lastLoggedInString = `${translate('resources.users.fields.lastActiveAt')}: 
          ${moment(lastLoggedIn).format('DD-MM-YYYY HH:mm')} - ${moment(lastLoggedIn).locale(locale[0]).fromNow()} `;

          setUserLastLoggedIn(lastLoggedInString);

        }

      });

    }

  }, [record, locale]); // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();
  const AssetToolBar = (props: any) => {

    return (
      <FormToolbar {...props}>
        <BhvkBackButton onClick={() => navigate('/assets', { replace: true })}/>
        <BhvkSaveButton/>
      </FormToolbar>
    );

  };

  if (!record) {

    return null;

  }
  return (
    <SimpleForm toolbar={<AssetToolBar/>}>
      {userLastLoggedIn ? (
        <Tooltip title={userLastLoggedIn} placement={'bottom'} arrow>
          <h4 style={{ marginBottom: '10px' }}>"{record.name}" {translate('general.text.settings')}</h4>
        </Tooltip>
      ) : (
        <h4 style={{ marginBottom: '10px' }}>"{record.name}" {translate('general.text.settings')}</h4>
      )}
      <hr/>
      <Grid container spacing={3} alignItems="center" sx={{ width: '100%', maxWidth: '1000px' }}>
        <Grid>
          <AvatarImageInput source="properties.image" />
        </Grid>
        <Grid
          md={7.5}
          sx={{
            '& .input-label': {
              fontSize: '20px',
            },
            '& .bhvk-gray-input .MuiOutlinedInput-root': {
              height: '64px',
            },
          }}
        >
          <GrayTextInputWithUpperLabel
            source="name"
            label="general.fields.name"
            fullWidth
            validate={required()}
          />
        </Grid>
      </Grid>
      <hr/>
      <Grid container spacing={3} sx={{ width: '100%', maxWidth: '800px' }}>
        <Grid md={6}>
          <InputLabel className="input-label">
            {translate('resources.users.fields.username')}
          </InputLabel>
          <ReferenceOneInput reference="users" target="_id" source="userId" fullWidth>
            <GrayTextInputWithUpperLabel source="username" fullWidth label="" disabled/>
          </ReferenceOneInput>
        </Grid>
      </Grid>
      <hr/>
      <Typography variant="h6" gutterBottom>
        {translate('general.text.contact_info')}
      </Typography>
      <Grid container spacing={3} sx={{ width: '100%', maxWidth: '800px' }}>
        <Grid md={6}>
          <GrayTextInputWithUpperLabel
            source="properties.email"
            label="resources.assets.fields.email"
            fullWidth
            validate={email()}
          />
        </Grid>
        <Grid md={6}>
          <PhoneNumberInput source="properties.phoneNumber" fullWidth colorVariant="gray" required/>
        </Grid>
      </Grid>
      <hr/>
      {(groupsRequest.data?.length || 0) > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            {translate('menu.items.groups')}
          </Typography>
          <Grid container spacing={3} sx={{ width: '100%', maxWidth: '800px' }}>
            <Grid md={12}>
              <InputLabel className="input-label">
                {translate('menu.items.groups')}
              </InputLabel>
              <ReferenceArrayInput source="assetGroups" reference="asset-groups" label="menu.items.groups" fullWidth>
                <GrayAutocompleteArrayInputWithUpperLabel fullWidth label={false} sx={{
                  '& fieldset': {
                    borderRadius: '5px',
                    border: 'none',
                  },
                }}
                />
              </ReferenceArrayInput>
            </Grid>
          </Grid>
          <hr/>
        </>
      )}
      <Typography variant="h6" gutterBottom marginTop={'10px'}>
        {translate('resources.qr-templates.text.options')}
      </Typography>
      <div style={{ width: '100%' }}>
        <Grid container spacing={2} justifyContent="center" sx={{ width: '100%', maxWidth: '800px' }}>
          <Grid md={6}>
            <InputLabel className="input-label">
              {translate('resources.users.text.access_to_dashboard')}
            </InputLabel>
            <IsAdminBooleanSetterField source={''} label="resources.users.text.access_to_dashboard" changeHandler={() => {

              setUpdatedAt(new Date());

            }}/>
          </Grid>
          <Grid md={6}>
            <FormDataConsumer>
              {({ formData }) => {

                return (
                  <BooleanCheckBoxInput
                    source="available"
                    label="resources.users.text.receives_alarms"
                    disabled={!formData.properties?.phoneNumber || formData.properties?.phoneNumber === '+31'}
                    disabledText={translate('resources.app-users.text.fill_phonenumber')}
                  />
                );

              }}
            </FormDataConsumer>
          </Grid>
        </Grid>
      </div>
      <hr/>
      <Typography variant="h6" gutterBottom marginTop={'10px'}>
        {translate('general.text.actions')}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2}>
        <ResetButtonPreparer updatedAt={updatedAt}/>
        <AssetDeleteButton/>
        {currentUserId === record.user._id && (
          <TwoFactorButton
            isAsset
            username={userName}
            buttonProps={{
              variant: 'contained',
              color: 'primary',
              size: 'medium',
            }}/>
        )}
      </Stack>
      {certificationsEnabled && (
        <>
          <hr style={{ marginTop: '20px' }}/>
          <CertificationList/>
        </>
      )}
    </SimpleForm>
  );

};

export const AssetEdit = () => {

  const notify = useNotify();
  const refresh = useRefresh();
  const { refetch } = useGetIdentity();
  const [needsContextRefresh, setNeedsContextRefresh] = useState(false);
  const editSuccess = async () => {

    notify('resources.users.text.saving_successful');
    if (needsContextRefresh) {

      await refreshContext();
      if (refetch) {

        refetch();

      }

    }
    refresh();

  };
  return (
    <EditWithSuccess actions={false} component={PageContentCard} transform={assetUserEditTransformer} mutationOptions={{ onSuccess: editSuccess }}>
      <AssetEditForm setNeedsContextRefresh={setNeedsContextRefresh}/>
    </EditWithSuccess>
  );

};

export const AssetCreate = () => {

  const notify = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(null);
  const assetCreateOnSuccess = (data: any) => {

    if (data.error) {

      notify(data.message, { type: 'error' });
      return;

    }
    notify('ra.notification.created', { messageArgs: { smart_count: 1 } });
    redirect('edit', 'assets', data.id, data);

  };

  const translate = useTranslate();
  return (
    <Create
      component={PageContentCard}
      transform={assetUserTransformer}
      actions={false}
      mutationOptions={{ meta: { needsUserCreation: true, code }, onSuccess: assetCreateOnSuccess }}
    >
      <h4 style={{ marginBottom: '10px' }}>{translate('resources.assets.text.add_user')}</h4>
      <hr/>
      <SimpleForm
        toolbar={(
          <FormToolbar sx={{ maxWidth: '500px' }}>
            <BhvkBackButton onClick={() => navigate('/assets', { replace: true })}/>
            <BhvkSaveButton/>
          </FormToolbar>
        )}
        sx={{ maxWidth: '1000px' }}
      >
        <Stack spacing={1} sx={{ width: '500px' }}>
          <RegistrationCodePicker setCode={setCode}/>
          <GrayTextInputWithUpperLabel
            source="name"
            label="general.fields.name"
            fullWidth
            validate={required()}
            isRequired
          />
          <PhoneNumberInput
            source="properties.phoneNumber"
            label="resources.assets.fields.phoneNumber"
            fullWidth
            colorVariant="gray"
            required
          />
          <GrayTextInputWithUpperLabel
            source="properties.email"
            label="resources.assets.fields.email"
            fullWidth
            validate={[email(), required()]}
            isRequired
          />
          <UsernameInput variant="bhvk"/>
          <BooleanInput source="available" label="resources.users.text.receives_alarms" name="available" defaultValue={true} defaultChecked={true}/>
          <BooleanInput label="resources.users.text.access_to_dashboard" source="isAdmin"/>
        </Stack>
      </SimpleForm>
    </Create>
  );

};

const UserCertificationToolbar = (props: { assetId: string }) => {

  const navigate = useNavigate();

  return (
    <FormToolbar sx={{ maxWidth: '500px' }}>
      <BhvkBackButton onClick={() => navigate(`/assets/${props.assetId}`, { replace: true })}/>
      <BhvkSaveButton />
    </FormToolbar>
  );

};

const CertificationCategoryAutocomplete = () => {

  const { setValue, resetField, getValues } = useFormContext();
  const { dirtyFields } = useFormState();
  const { allChoices, isPending } = useChoicesContext();
  useEffect(() => {

    if (isPending) return;

    const existing = getValues('certificationCategory._id');

    if (dirtyFields.certificationCategory) return;
    if (existing != null && existing !== '') return;

    const first = allChoices?.[0];
    if (!first?._id) return;

    setValue('certificationCategory._id', first._id, { shouldDirty: false });
    setValue('certificationCategory', first, { shouldDirty: false });

  }, [allChoices, getValues, setValue, dirtyFields.certificationCategory, isPending]);

  return (
    <GrayAutocompleteInputWithUpperLabel
      label="resources.assets.certifications.text.title_single"
      optionText="name"
      validate={required()}
      isRequired
      fullWidth
      helperText={false}
      onChange={(_value, record) => {

        setValue('certificationCategory', record);

        if (!(record as unknown as CertificationCategory)?.isDocumentaryProofRequired) {

          resetField('document');

        }

      }}
    />
  );

};

const CertificationCategoryInput = () => (
  <ReferenceInput
    source="certificationCategory._id"
    reference="certificates"
    sort={{ field: 'createdAt', order: 'DESC' }}
  >
    <CertificationCategoryAutocomplete />
  </ReferenceInput>
);

export const UserCertificationCreate = () => {

  const { id: assetId } = useParams();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const { permissions, isPending } = usePermissions();

  if (!assetId || isPending) {

    return null;

  }

  // fix permissions bypass issue that is caused by the certifications create route being a nested route of the assets resource
  if (!canAccess({
    permissions,
    action: 'create',
    resource: 'user-certifications',
  })) {

    redirect(`/assets/${assetId}`);
    return null;

  }

  const OnSuccess = () => {

    notify(`${translate('resources.assets.certifications.text.title_single')} ${translate('general.text.saved').toLowerCase()}`, { type: 'success' });
    redirect(`/assets/${assetId}`);

  };

  return (
    <CreateWithSuccess
      component={PageContentCard}
      resource="user-certifications"
      redirect={`/assets/${assetId}`}
      transform={userCertificationTransformer}
      mutationOptions={{ meta: { assetId }, onSuccess: OnSuccess }}
    >
      <h4 style={{ marginBottom: '10px' }}>
        {translate('resources.assets.certifications.text.add_certification')}
      </h4>
      <hr/>
      <SimpleForm
        toolbar={<UserCertificationToolbar assetId={assetId}/>}
        sx={{ maxWidth: '1000px' }}
      >
        <Stack sx={{ width: '100%', maxWidth: '30rem' }} spacing={3}>
          <CertificationCategoryInput />
          <GrayDateInputWithUpperLabel
            source="firstIssuedOn"
            label="resources.assets.certifications.fields.first_issued_on"
            validate={[
              required(),
              notLaterThanToday(
                'resources.assets.certifications.validation.first_issued_in_future',
              ),
              notLaterThan(
                'lastReceivedOn',
                'resources.assets.certifications.validation.first_issued_later_than_last_received',
              ),
            ]}
            isRequired
            fullWidth
            helperText={false}
          />
          <FormDataConsumer<{ certificationCategory: CertificationCategory | undefined }>>
            {({ formData }) => {

              return (
                <ExpiryDateDisplay
                  startDateSource="lastReceivedOn"
                  expiryDateSource="expiryDate"
                  timeGranularity={formData.certificationCategory?.validityPeriod}
                  startDateLabel="resources.assets.certifications.fields.last_received_on"
                  expiryDateLabel="resources.assets.certifications.fields.expiry_date"
                  helperText={translate('resources.assets.certifications.text.expiry_date_input_helper_text')}
                  usage="certifications"
                  minDateSource="firstIssuedOn"
                  isRequired
                />
              );

            }}
          </FormDataConsumer>
          <FormDataConsumer<{ certificationCategory: CertificationCategory | undefined }>>
            {({ formData }) => {

              if (formData.certificationCategory?.isDocumentaryProofRequired) {

                return (
                  <>
                    <InputLabel className="input-label">
                      {translate('resources.assets.certifications.fields.documentary_proof')}
                    </InputLabel>
                    <DocumentaryProofInputPreview
                      source="document"
                      imageStyle={{
                        height: '600px',
                        marginBottom: '10px',
                        objectPosition: 'left',
                      }}
                      pdfStyle={{
                        height: '800px', marginBottom: '10px',
                      }}
                    />
                    <CertificationDocumentInput
                      source="document"
                      label={false}
                      labelSingle={translate('resources.assets.certifications.text.drop_document_to_upload')}
                      validate={required()}
                      accept={DOCUMENT_ACCEPT}
                    />
                  </>
                );

              }

              return null;

            }}
          </FormDataConsumer>
        </Stack>
      </SimpleForm>
    </CreateWithSuccess>
  );

};

export const UserCertificationEdit = () => {

  const { id: assetId, certificateId: certificationId } = useParams();
  const location = useLocation();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const { permissions, isPending } = usePermissions();
  const [record, setRecord] = useState<AssetCertification | undefined>();
  const [assetName, setAssetName] = useState<string | undefined>();
  const [certificationCategory, setCertificationCategory] = useState<CertificationCategory | undefined>();

  const { data: usersData } = useGetList(appUsersResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  useEffect(() => {

    if (!certificationId || !assetId || isPending) {

      return;

    }

    // fix permissions bypass issue that is caused by the certifications edit route being a nested route of the assets resource
    if (!canAccess({
      permissions,
      action: 'edit',
      resource: 'user-certifications',
    })) {

      redirect(`/assets/${assetId}`);
      return;

    }

    if (location.state?.record) {

      setRecord(location.state.record);

    }

    if (location.state?.assetName) {

      setAssetName(location.state.assetName);

    }

    if (!location.state?.record) {

      // Fallback to fetch the record in case it's not in the location state. This can happen when the user navigates to the page using a link.
      xgacDataProvider.getOne('user-certifications', {
        id: certificationId,
        meta: { assetId },
      }).then((response) => {

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // user certification is not a standalone resource and therefore cannot be 'safely' typed
        setRecord(response.data as AssetCertification);

      }).catch((error) => {

        notifyWithError(notify, error);
        redirect(`/assets/${assetId}`);

      });

    }

    if (!location.state?.assetName) {

      xgacDataProvider.getOne('assets', {
        id: assetId,
      }).then((response) => {

        setAssetName(response.data?.name);

      }).catch((error) => {

        notifyWithError(notify, error);

      });

    }

  }, [assetId, certificationId, notify, redirect, isPending, permissions, location.state?.record, location.state?.assetName]);

  useEffect(() => {

    if (!record) {

      return;

    }

    xgacDataProvider.getOne('certificates', {
      id: record.certificationCategory._id,
    }).then((response) => {

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setCertificationCategory(response.data as CertificationCategory);

    }).catch((error) => {

      notifyWithError(notify, error);
      redirect(`/assets/${assetId}`);

    });

  }, [record, notify, redirect, assetId]);

  if (!assetId || !certificationId || !record) {

    return null;

  }

  const onSubmit = async (values: FieldValues) => {

    if (!record) {

      return;

    }

    try {

      const updatedCertification = userCertificationTransformer({
        ...record,
        ...values,
      });

      await xgacDataProvider.update('user-certifications', {
        id: certificationId,
        data: updatedCertification,
        previousData: record,
        meta: { assetId, certificationId },
      });

      notify(`${translate('resources.assets.certifications.text.title_single')} ${translate('general.text.updated')}`, { type: 'success' });
      redirect(`/assets/${assetId}`);

    } catch (error) {

      notifyWithError(notify, error);

    }

  };

  return (
    <PageContentCard>
      <Form onSubmit={onSubmit} record={record}>
        <h4 style={{ marginBottom: '10px' }}>
          {translate(
            'resources.assets.certifications.text.edit_certification_title',
            { category: certificationCategory?.name, user_name: assetName ?? '' },
          )}
        </h4>
        <hr style={{ width: '100%' }}/>
        <CardContent sx={{ maxWidth: '1000px' }}>
          <Stack alignItems="flex-start">
            <Stack sx={{ width: 'fit-content', maxWidth: '30rem' }} spacing={3}>
              <GrayDateInputWithUpperLabel
                source="firstIssuedOn"
                isRequired
                label="resources.assets.certifications.fields.first_issued_on"
                validate={[
                  required(),
                  notLaterThanToday(
                    'resources.assets.certifications.validation.first_issued_in_future',
                  ),
                  notLaterThan(
                    'lastReceivedOn',
                    'resources.assets.certifications.validation.first_issued_later_than_last_received',
                  ),
                ]}
                helperText={false}
                fullWidth
              />
              <ExpiryDateDisplay
                startDateSource="lastReceivedOn"
                expiryDateSource="expiryDate"
                isRequired
                timeGranularity={certificationCategory?.validityPeriod}
                startDateLabel="resources.assets.certifications.fields.last_received_on"
                expiryDateLabel="resources.assets.certifications.fields.expiry_date"
                helperText={translate('resources.assets.certifications.text.expiry_date_input_helper_text')}
                usage="certifications"
                minDateSource="firstIssuedOn"
              />
              <GrayDateInputWithUpperLabel
                source="plannedRenewalDate"
                label="resources.assets.certifications.fields.planned_renewal_date"
                helperText="resources.assets.certifications.text.planned_renewal_date_helper_text"
                size="medium"
                fullWidth
                inputProps={{ min: getMinimumNextDate() }}
                FormHelperTextProps={{
                  sx: {
                    mt: 0.5,
                    marginLeft: 0,
                    fontSize: '0.625rem',
                  },
                }}
              />
              {certificationCategory?.isDocumentaryProofRequired && (
                <>
                  <InputLabel className="input-label" required={true}>
                    {translate('resources.assets.certifications.fields.documentary_proof')}
                  </InputLabel>
                  <DocumentaryProofInputPreview
                    source="document"
                    existingDocumentaryProof={record.documentaryProof}
                    imageStyle={{
                      height: '600px',
                      marginBottom: '10px',
                      objectPosition: 'left',
                    }}
                    pdfStyle={{
                      height: '800px', marginBottom: '10px',
                    }}
                  />
                  <CertificationDocumentInput
                    source="document"
                    label={false}
                    validate={record.documentaryProof ? undefined : required()}
                    labelSingle={translate('resources.assets.certifications.text.drop_document_to_upload')}
                    accept={DOCUMENT_ACCEPT}
                  />
                </>
              )}
              <NextEmailAlert type="certification" category={certificationCategory ?? null} users={usersData ?? []} certifiedUserName={assetName ?? null} />
            </Stack>
          </Stack>
        </CardContent>
        <UserCertificationToolbar assetId={assetId}/>
      </Form>
    </PageContentCard>

  );

};
